<template>
  <HrPageLayout
    title="Assign Employee Salaries"
    subtitle="Set basic pay and effective dates for active employees before running payroll"
    icon="bx bx-wallet"
    :breadcrumb-items="[{ label: 'Payroll' }, { label: 'Assign Salaries' }]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-outline-secondary" @click="$router.push('/payroll')">
        <i class="bx bx-arrow-back"></i> Back to payroll
      </button>
    </template>

    <div class="salary-assign-page">
      <!-- Summary metrics -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-group"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Employees listed</p>
          <h2 class="hr-metric-value">{{ totalItems }}</h2>
            </div>
          <p class="hr-metric-subtext">Matching current filters</p>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">With salary</p>
          <h2 class="hr-metric-value">{{ withSalaryCount }}</h2>
            </div>
          <p class="hr-metric-subtext">{{ salaryCoveragePercent }}% of listed</p>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Needs assignment</p>
          <h2 class="hr-metric-value">{{ withoutSalaryCount }}</h2>
            </div>
          <p class="hr-metric-subtext">
            <button
              v-if="withoutSalaryCount > 0"
              type="button"
              class="salary-link-btn"
              @click="filterNeedsSalary"
            >
              Show only these
            </button>
            <span v-else>All listed have salary</span>
          </p>
        </div>
      </div>

      <!-- Coverage bar (listed set) -->
      <div v-if="totalItems > 0" class="hr-chart-card hr-mb-4 salary-coverage-card">
        <div class="hr-chart-body salary-coverage-body">
          <div class="salary-coverage-header">
            <span class="salary-coverage-title">Salary assignment coverage</span>
            <span class="salary-coverage-pct">{{ salaryCoveragePercent }}%</span>
          </div>
          <div class="salary-coverage-track" role="progressbar" :aria-valuenow="salaryCoveragePercent" aria-valuemin="0" aria-valuemax="100">
            <div
              class="salary-coverage-fill"
              :style="{ width: salaryCoveragePercent + '%' }"
              :class="coverageBarClass"
            ></div>
          </div>
        </div>
      </div>

      <!-- Pay components info -->
      <div v-if="filters.payrollConfigId" class="hr-chart-card hr-mb-4 salary-components-banner">
        <div class="hr-chart-body salary-components-banner-body">
          <i class="bx bx-layer"></i>
          <div>
            <strong>Payroll Hub components</strong>
            <p>Select an employee on the right, then use the <strong>Pay components</strong> tab to add recurring allowances/deductions and one-off period adjustments for Payroll Hub runs.</p>
          </div>
        </div>
      </div>
      <div v-else class="hr-chart-card hr-mb-4 salary-components-banner warn">
        <div class="hr-chart-body salary-components-banner-body">
          <i class="bx bx-error-circle"></i>
          <div>
            <strong>Select a payroll group</strong>
            <p>Choose a payroll group above to manage pay components and adjustments per employee.</p>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-header">
          <h3 class="hr-chart-title"><i class="bx bx-filter-alt"></i> Find employees</h3>
        </div>
        <div class="hr-chart-body">
          <div class="salary-quick-filters hr-mb-4">
            <span class="salary-quick-label">Quick filter</span>
            <button
              type="button"
              class="salary-chip"
              :class="{ active: filters.hasSalary === '' }"
              @click="setSalaryFilter('')"
            >
              All active
            </button>
            <button
              type="button"
              class="salary-chip"
              :class="{ active: filters.hasSalary === '1' }"
              @click="setSalaryFilter('1')"
            >
              <i class="bx bx-check"></i> With salary
            </button>
            <button
              type="button"
              class="salary-chip salary-chip-warn"
              :class="{ active: filters.hasSalary === '0' }"
              @click="setSalaryFilter('0')"
            >
              <i class="bx bx-error"></i> Without salary
            </button>
          </div>

          <div class="hr-filters-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">Payroll group</label>
              <select v-model="filters.payrollConfigId" class="hr-select" @change="onPayrollGroupChange">
                <option value="">All active employees</option>
                <option v-for="c in payrollConfigs" :key="c.id" :value="String(c.id)">
                  {{ c.config_name }} ({{ c.employee_count || 0 }} members)
                </option>
              </select>
            </div>
            <div class="hr-filter-group salary-search-group">
              <label class="hr-form-label">Search</label>
              <div class="salary-search-wrap">
                <i class="bx bx-search salary-search-icon"></i>
                <input
                  v-model="filters.keyword"
                  type="search"
                  class="hr-input salary-search-input"
                  placeholder="Name, employee no., email, department..."
                  @input="debouncedLoad"
                  @keyup.enter="loadEmployees"
                />
              </div>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select v-model="filters.departmentId" class="hr-select" @change="loadEmployees">
                <option value="">All departments</option>
                <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.departmentName }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Salary status</label>
              <select v-model="filters.hasSalary" class="hr-select" @change="loadEmployees">
                <option value="">All active employees</option>
                <option value="1">With salary assigned</option>
                <option value="0">Without salary</option>
              </select>
            </div>
            <div class="hr-filter-group salary-filter-actions">
              <label class="hr-form-label hr-filter-actions-label">&nbsp;</label>
              <div class="salary-filter-btns">
                <button type="button" class="hr-btn hr-btn-primary" :disabled="loading" @click="loadEmployees">
                  <span v-if="loading" class="hr-inline-spinner"></span>
                  <i v-else class="bx bx-search"></i>
                  {{ loading ? "Loading…" : "Apply" }}
                </button>
                <button type="button" class="hr-btn hr-btn-outline-secondary" :disabled="loading" @click="resetFilters">
                  <i class="bx bx-refresh"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div v-if="loading" class="hr-loading-state hr-py-5">
        <div class="hr-spinner"></div>
        <p>Loading employees…</p>
      </div>

      <div v-else-if="employees.length === 0" class="hr-chart-card hr-text-center salary-empty">
        <div class="hr-chart-body hr-py-5">
          <i class="bx bx-user-x salary-empty-icon"></i>
          <h5 class="salary-empty-title">No employees match your filters</h5>
          <p class="hr-text-muted">
            <template v-if="filters.payrollConfigId && payrollConfigs.length">
              No members in this payroll group yet.
              <router-link to="/payroll-setup">Assign employees to the group</router-link>
              or clear the group filter.
            </template>
            <template v-else>
              Try clearing search or department filters, or view all active staff.
            </template>
          </p>
          <button type="button" class="hr-btn hr-btn-primary hr-mt-3" @click="resetFilters">
            <i class="bx bx-refresh"></i> Clear filters
          </button>
        </div>
      </div>

      <div v-else class="salary-master-detail">
        <!-- Employee list -->
        <div class="salary-list-panel hr-chart-card">
          <div class="hr-chart-header salary-list-header">
            <h3 class="hr-chart-title"><i class="bx bx-user"></i> Employees</h3>
            <span class="salary-list-count">{{ employees.length }}</span>
          </div>
          <div class="salary-list-body">
            <button
              v-for="emp in employees"
              :key="emp.id"
              type="button"
              class="salary-emp-card"
              :class="{
                selected: selectedEmployee?.id === emp.id,
                pending: !emp.salary_id
              }"
              @click="selectEmployee(emp)"
            >
              <div class="salary-avatar" :class="emp.salary_id ? 'has-salary' : 'no-salary'">
                {{ initials(emp) }}
              </div>
              <div class="salary-emp-card-body">
                <div class="salary-emp-card-top">
                  <span class="salary-emp-name">{{ fullName(emp) }}</span>
                  <span
                    class="salary-status-dot"
                    :class="emp.salary_id ? 'assigned' : 'pending'"
                    :title="emp.salary_id ? 'Salary assigned' : 'Needs salary'"
                  ></span>
                </div>
                <div class="salary-emp-card-meta">
                  <span v-if="emp.empNo">{{ emp.empNo }}</span>
                  <span v-if="emp.departmentName">{{ emp.departmentName }}</span>
                </div>
                <div v-if="emp.salary_id" class="salary-emp-card-salary">
                  {{ formatCurrency(emp.basic_salary) }}
                </div>
              </div>
              <i class="bx bx-chevron-right salary-emp-chevron"></i>
            </button>
          </div>
        </div>

        <!-- Detail panel -->
        <div class="salary-detail-panel hr-chart-card">
          <template v-if="selectedEmployee">
            <div class="salary-detail-hero">
              <div class="salary-detail-hero-bg"></div>
              <div class="salary-detail-hero-content">
                <div class="salary-detail-avatar-wrap">
                  <div
                    class="salary-detail-avatar"
                    :class="selectedEmployee.salary_id ? 'has-salary' : 'no-salary'"
                  >
                    {{ initials(selectedEmployee) }}
                  </div>
                  <span
                    class="salary-detail-status-badge"
                    :class="selectedEmployee.salary_id ? 'assigned' : 'pending'"
                  >
                    <i class="bx" :class="selectedEmployee.salary_id ? 'bx-check-circle' : 'bx-error-circle'"></i>
                    {{ selectedEmployee.salary_id ? 'Salary assigned' : 'Needs assignment' }}
                  </span>
                </div>
                <div class="salary-detail-identity">
                  <h2 class="salary-detail-name">{{ fullName(selectedEmployee) }}</h2>
                  <div class="salary-detail-chips">
                    <span v-if="selectedEmployee.empNo" class="salary-detail-chip">
                      <i class="bx bx-id-card"></i> {{ selectedEmployee.empNo }}
                    </span>
                    <span v-if="selectedEmployee.departmentName" class="salary-detail-chip">
                      <i class="bx bx-buildings"></i> {{ selectedEmployee.departmentName }}
                    </span>
                    <span v-if="selectedEmployee.email" class="salary-detail-chip">
                      <i class="bx bx-envelope"></i> {{ selectedEmployee.email }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="salary-detail-stats">
              <div class="salary-stat-card">
                <div class="salary-stat-icon earn"><i class="bx bx-wallet"></i></div>
                <div>
                  <span class="salary-stat-label">Basic salary</span>
                  <span class="salary-stat-value" :class="{ muted: !selectedEmployee.salary_id }">
                    {{ selectedEmployee.salary_id ? formatCurrency(selectedEmployee.basic_salary) : 'Not set' }}
                  </span>
                </div>
              </div>
              <div class="salary-stat-card">
                <div class="salary-stat-icon date"><i class="bx bx-calendar"></i></div>
                <div>
                  <span class="salary-stat-label">Effective from</span>
                  <span class="salary-stat-value" :class="{ muted: !selectedEmployee.salary_effective_date }">
                    {{ selectedEmployee.salary_effective_date ? formatDate(selectedEmployee.salary_effective_date) : '—' }}
                  </span>
                </div>
              </div>
              <div class="salary-stat-card">
                <div class="salary-stat-icon group"><i class="bx bx-group"></i></div>
                <div>
                  <span class="salary-stat-label">Payroll group</span>
                  <span class="salary-stat-value" :class="{ muted: !filters.payrollConfigId }">
                    {{ selectedPayrollGroupName }}
                  </span>
                </div>
              </div>
            </div>

            <div class="salary-detail-tabs">
              <button
                type="button"
                class="salary-detail-tab"
                :class="{ active: detailTab === 'salary' }"
                @click="detailTab = 'salary'"
              >
                <i class="bx bx-wallet-alt"></i> Basic salary
              </button>
              <button
                type="button"
                class="salary-detail-tab"
                :class="{ active: detailTab === 'components', disabled: !filters.payrollConfigId }"
                :disabled="!filters.payrollConfigId"
                :title="filters.payrollConfigId ? '' : 'Select a payroll group first'"
                @click="detailTab = 'components'"
              >
                <i class="bx bx-layer"></i> Pay components
              </button>
            </div>

            <div class="salary-detail-body">
              <div v-if="detailTab === 'salary'" class="salary-tab-salary">
                <div v-if="!selectedEmployee.salary_id" class="salary-cta-card warn">
                  <div class="salary-cta-icon"><i class="bx bx-error-circle"></i></div>
                  <div>
                    <strong>No basic salary assigned</strong>
                    <p>Set the employee's basic pay and effective date before running payroll in Payroll Hub.</p>
                  </div>
                  <button
                    type="button"
                    class="salary-cta-btn primary"
                    :disabled="openingModalFor === selectedEmployee.id"
                    @click="openSalaryModal(selectedEmployee)"
                  >
                    <span v-if="openingModalFor === selectedEmployee.id" class="hr-inline-spinner"></span>
                    <i v-else class="bx bx-plus"></i>
                    Assign salary
                  </button>
                </div>
                <div v-else class="salary-cta-card success">
                  <div class="salary-cta-icon"><i class="bx bx-check-circle"></i></div>
                  <div>
                    <strong>{{ formatCurrency(selectedEmployee.basic_salary) }} / month</strong>
                    <p>
                      Effective {{ formatDate(selectedEmployee.salary_effective_date) }}.
                      Edit to change basic pay, pay grade, or legacy allowances.
                    </p>
                  </div>
                  <button
                    type="button"
                    class="salary-cta-btn outline"
                    :disabled="openingModalFor === selectedEmployee.id"
                    @click="openSalaryModal(selectedEmployee)"
                  >
                    <span v-if="openingModalFor === selectedEmployee.id" class="hr-inline-spinner dark"></span>
                    <i v-else class="bx bx-edit"></i>
                    Edit salary
                  </button>
                </div>
              </div>

              <div v-else-if="!filters.payrollConfigId" class="salary-tab-empty">
                <i class="bx bx-group"></i>
                <p>Select a payroll group in the filters above to manage pay components.</p>
              </div>

              <EmployeePayComponentsPanel
                v-else
                embedded
                :employee="selectedEmployee"
                :payroll-config-id="filters.payrollConfigId"
                :payroll-configs="payrollConfigs"
                @saved="loadEmployees"
              />
            </div>
          </template>

          <div v-else class="salary-detail-empty">
            <div class="salary-detail-empty-icon"><i class="bx bx-user-pin"></i></div>
            <h4>Select an employee</h4>
            <p>Choose someone from the list to assign salary and manage pay components.</p>
          </div>
        </div>
      </div>
    </div>

    <EmployeePayComponentsPanel
      v-model="componentsPanelOpen"
      :employee="componentsEmployee"
      :payroll-config-id="filters.payrollConfigId"
      :payroll-configs="payrollConfigs"
      @saved="loadEmployees"
    />

    <EmployeeSalary
      ref="employeeSalaryRef"
      :employee-id="selectedEmployeeId"
      @salary-updated="onSalaryUpdated"
      @modal-closed="onModalClosed"
    />
  </HrPageLayout>
</template>

<script>
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import EmployeeSalary from "@/components/payroll/EmployeeSalary.vue";
import EmployeePayComponentsPanel from "@/components/payroll/EmployeePayComponentsPanel.vue";

let searchDebounceTimer = null;

export default {
  name: "AssignEmployeeSalaries",
  components: {
    HrPageLayout,
    EmployeeSalary,
    EmployeePayComponentsPanel
  },
  data() {
    return {
      loading: false,
      employees: [],
      departments: [],
      payrollConfigs: [],
      totalItems: 0,
      filters: {
        keyword: "",
        departmentId: "",
        hasSalary: "",
        payrollConfigId: ""
      },
      selectedEmployeeId: null,
      selectedEmployee: null,
      detailTab: "salary",
      openingModalFor: null,
      componentsPanelOpen: false,
      componentsEmployee: null
    };
  },
  computed: {
    withSalaryCount() {
      return this.employees.filter((e) => e.salary_id).length;
    },
    withoutSalaryCount() {
      return this.employees.filter((e) => !e.salary_id).length;
    },
    salaryCoveragePercent() {
      if (!this.totalItems) return 0;
      return Math.round((this.withSalaryCount / this.totalItems) * 100);
    },
    coverageBarClass() {
      const p = this.salaryCoveragePercent;
      if (p >= 90) return "fill-success";
      if (p >= 60) return "fill-primary";
      return "fill-warning";
    },
    selectedPayrollGroupName() {
      if (!this.filters.payrollConfigId) return "Not selected";
      const g = this.payrollConfigs.find(
        (c) => String(c.id) === String(this.filters.payrollConfigId)
      );
      return g?.config_name || "Selected group";
    }
  },
  mounted() {
    this.loadDepartments();
    this.loadPayrollConfigs();
  },
  beforeUnmount() {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
  },
  methods: {
    loadDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
        .then((res) => {
          if (res.data?.success && res.data.data?.departments) {
            this.departments = res.data.data.departments;
          }
        })
        .catch(() => {});
    },

    loadPayrollConfigs() {
      const data = new FormData();
      data.append("function", "getPayrollConfigs");
      execute(data, authUrl)
        .then((res) => {
          if (res.data?.success) {
            this.payrollConfigs = res.data.data?.payroll_configs || [];
            const defaultId = res.data.data?.default_config_id;
            const fromQuery = this.$route?.query?.config_id;
            if (fromQuery) {
              this.filters.payrollConfigId = String(fromQuery);
            } else if (defaultId) {
              this.filters.payrollConfigId = String(defaultId);
            } else if (this.payrollConfigs.length === 1) {
              this.filters.payrollConfigId = String(this.payrollConfigs[0].id);
            }
          }
        })
        .catch(() => {})
        .finally(() => this.loadEmployees());
    },

    onPayrollGroupChange() {
      this.loadEmployees();
    },

    loadEmployees() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getActiveEmployeesForSalaryAssign");
      data.append("keyword", this.filters.keyword || "");
      data.append("departmentId", this.filters.departmentId || "");
      data.append("hasSalary", this.filters.hasSalary || "");
      if (this.filters.payrollConfigId) {
        data.append("payroll_config_id", this.filters.payrollConfigId);
      }

      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (res.data?.success) {
            this.employees = res.data.data.employees || [];
            this.totalItems = res.data.data.totalItems ?? this.employees.length;
            this.syncSelectedEmployee();
          } else {
            this.employees = [];
            this.totalItems = 0;
            this.selectedEmployee = null;
          }
        })
        .catch(() => {
          this.loading = false;
          this.employees = [];
          this.totalItems = 0;
          this.selectedEmployee = null;
        });
    },

    syncSelectedEmployee() {
      if (!this.employees.length) {
        this.selectedEmployee = null;
        return;
      }
      if (this.selectedEmployee) {
        const updated = this.employees.find((e) => e.id === this.selectedEmployee.id);
        if (updated) {
          this.selectedEmployee = updated;
          return;
        }
      }
      this.selectedEmployee = this.employees[0];
    },

    selectEmployee(emp) {
      this.selectedEmployee = emp;
      if (!emp.salary_id) {
        this.detailTab = "salary";
      }
    },

    debouncedLoad() {
      if (searchDebounceTimer) clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(() => this.loadEmployees(), 350);
    },

    setSalaryFilter(value) {
      this.filters.hasSalary = value;
      this.loadEmployees();
    },

    filterNeedsSalary() {
      this.filters.hasSalary = "0";
      this.loadEmployees();
    },

    resetFilters() {
      this.filters.keyword = "";
      this.filters.departmentId = "";
      this.filters.hasSalary = "";
      this.filters.payrollConfigId = this.payrollConfigs.length === 1
        ? String(this.payrollConfigs[0].id)
        : "";
      this.loadEmployees();
    },

    fullName(emp) {
      const parts = [emp.firstName, emp.middleName, emp.lastName].filter(Boolean);
      return parts.join(" ") || "—";
    },

    initials(emp) {
      const a = (emp.firstName || "").charAt(0);
      const b = (emp.lastName || "").charAt(0);
      const s = (a + b).toUpperCase();
      return s || "?";
    },

    formatCurrency(val) {
      return new Intl.NumberFormat("en-KE", {
        style: "currency",
        currency: "KES",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(val || 0);
    },

    formatDate(dateString) {
      if (!dateString) return "—";
      return new Date(dateString).toLocaleDateString("en-KE", {
        year: "numeric",
        month: "short",
        day: "numeric"
      });
    },

    openComponentsPanel(employee) {
      if (!this.filters.payrollConfigId) return;
      this.componentsEmployee = employee;
      this.componentsPanelOpen = true;
    },

    openSalaryModal(employee) {
      this.selectEmployee(employee);
      this.detailTab = "salary";
      this.openingModalFor = employee.id;

      if (this.selectedEmployeeId === employee.id) {
        this.selectedEmployeeId = null;
        this.$nextTick(() => {
          this.selectedEmployeeId = employee.id;
          this.$nextTick(() => {
            this.$refs.employeeSalaryRef?.fetchEmployeeSalary(employee.id);
            setTimeout(() => {
              this.openingModalFor = null;
            }, 500);
          });
        });
      } else {
        this.selectedEmployeeId = employee.id;
        this.$nextTick(() => {
          setTimeout(() => {
            this.openingModalFor = null;
          }, 500);
        });
      }
    },

    onSalaryUpdated() {
      this.loadEmployees();
    },

    onModalClosed() {
      this.selectedEmployeeId = null;
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.salary-assign-page {
  max-width: 1400px;
}

.salary-coverage-card .hr-chart-body {
  padding: 16px 24px;
}

.salary-coverage-body {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.salary-coverage-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.salary-coverage-title {
  font-size: 14px;
  font-weight: 600;
  color: #475569;
}

.salary-coverage-pct {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.salary-coverage-track {
  height: 10px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.salary-coverage-fill {
  height: 100%;
  border-radius: 999px;
  transition: width 0.35s ease;
}

.salary-coverage-fill.fill-success {
  background: linear-gradient(90deg, #10b981, #34d399);
}

.salary-coverage-fill.fill-primary {
  background: linear-gradient(90deg, #4f46e5, #7c3aed);
}

.salary-coverage-fill.fill-warning {
  background: linear-gradient(90deg, #f59e0b, #fbbf24);
}

.salary-quick-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.salary-quick-label {
  font-size: 12px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-right: 4px;
}

.salary-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.salary-chip:hover {
  border-color: #c7d2fe;
  background: #f8fafc;
}

.salary-chip.active {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 4px 14px rgba(79, 70, 229, 0.25);
}

.salary-chip-warn.active {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 4px 14px rgba(245, 158, 11, 0.25);
}

.salary-search-wrap {
  position: relative;
}

.salary-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 18px;
  pointer-events: none;
}

.salary-search-input {
  padding-left: 42px;
}

.salary-search-group {
  grid-column: span 2;
}

@media (max-width: 768px) {
  .salary-search-group {
    grid-column: span 1;
  }
}

.salary-filter-actions-label {
  visibility: hidden;
}

.salary-filter-btns {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.salary-link-btn {
  background: none;
  border: none;
  padding: 0;
  color: #d97706;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: underline;
}

.salary-link-btn:hover {
  color: #b45309;
}

.salary-empty-icon {
  font-size: 56px;
  color: #cbd5e1;
}

.salary-empty-title {
  margin: 16px 0 8px;
  color: #1e293b;
}

.salary-emp-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.salary-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

.salary-avatar.has-salary {
  background: #d1fae5;
  color: #047857;
}

.salary-avatar.no-salary {
  background: #fef3c7;
  color: #b45309;
}

.salary-emp-name {
  font-weight: 600;
  color: #1e293b;
}

.salary-amount {
  font-weight: 700;
  color: #047857;
}

.salary-row-pending {
  background: #fffbeb;
}

.salary-row-pending:hover {
  background: #fef9c3 !important;
}

.hr-inline-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: salary-spin 0.7s linear infinite;
}

.hr-btn-outline-primary .hr-inline-spinner {
  border-color: rgba(79, 70, 229, 0.25);
  border-top-color: #4f46e5;
}

@keyframes salary-spin {
  to {
    transform: rotate(360deg);
  }
}

.hr-small {
  font-size: 12px;
}

.hr-p-0 {
  padding: 0 !important;
}

.hr-mt-3 {
  margin-top: 12px;
}

.hr-py-5 {
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.salary-components-banner .salary-components-banner-body {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding: 14px 18px;
}

.salary-components-banner .salary-components-banner-body i {
  font-size: 24px;
  color: #4f46e5;
  margin-top: 2px;
}

.salary-components-banner.warn .salary-components-banner-body i {
  color: #d97706;
}

.salary-components-banner p {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}

.salary-action-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: flex-end;
}

/* Master-detail layout */
.salary-master-detail {
  display: grid;
  grid-template-columns: minmax(280px, 340px) 1fr;
  gap: 20px;
  align-items: start;
}

@media (max-width: 1024px) {
  .salary-master-detail {
    grid-template-columns: 1fr;
  }
}

.salary-list-panel {
  position: sticky;
  top: 16px;
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (max-width: 1024px) {
  .salary-list-panel {
    position: static;
    max-height: 420px;
  }
}

.salary-list-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.salary-list-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4f46e5;
  font-size: 12px;
  font-weight: 700;
}

.salary-list-body {
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.salary-emp-card {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
}

.salary-emp-card:hover {
  border-color: #c7d2fe;
  background: #fafbff;
  box-shadow: 0 2px 8px rgba(79, 70, 229, 0.08);
}

.salary-emp-card.selected {
  border-color: #818cf8;
  background: linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%);
  box-shadow: 0 4px 16px rgba(79, 70, 229, 0.15);
}

.salary-emp-card.pending:not(.selected) {
  border-left: 3px solid #fbbf24;
}

.salary-emp-card-body {
  flex: 1;
  min-width: 0;
}

.salary-emp-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.salary-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.salary-status-dot.assigned {
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
}

.salary-status-dot.pending {
  background: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2);
}

.salary-emp-card-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
}

.salary-emp-card-meta span:not(:last-child)::after {
  content: "·";
  margin-left: 6px;
}

.salary-emp-card-salary {
  font-size: 13px;
  font-weight: 700;
  color: #059669;
  margin-top: 4px;
}

.salary-emp-chevron {
  color: #cbd5e1;
  font-size: 20px;
  flex-shrink: 0;
}

.salary-emp-card.selected .salary-emp-chevron {
  color: #6366f1;
}

/* Detail panel */
.salary-detail-panel {
  min-height: 480px;
  overflow: hidden;
}

.salary-detail-hero {
  position: relative;
  padding: 24px 24px 20px;
  overflow: hidden;
}

.salary-detail-hero-bg {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #a855f7 100%);
  opacity: 1;
}

.salary-detail-hero-bg::after {
  content: "";
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.salary-detail-hero-content {
  position: relative;
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.salary-detail-avatar-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.salary-detail-avatar {
  width: 72px;
  height: 72px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: 800;
  border: 3px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.salary-detail-avatar.has-salary {
  background: rgba(255, 255, 255, 0.95);
  color: #059669;
}

.salary-detail-avatar.no-salary {
  background: rgba(255, 255, 255, 0.95);
  color: #d97706;
}

.salary-detail-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.salary-detail-status-badge.assigned {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  backdrop-filter: blur(4px);
}

.salary-detail-status-badge.pending {
  background: rgba(251, 191, 36, 0.3);
  color: #fff;
}

.salary-detail-identity {
  flex: 1;
  min-width: 0;
  padding-top: 4px;
}

.salary-detail-name {
  margin: 0 0 10px;
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  letter-spacing: -0.02em;
}

.salary-detail-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.salary-detail-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.95);
  font-size: 12px;
  font-weight: 500;
  backdrop-filter: blur(4px);
}

.salary-detail-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 0 20px;
  margin-top: -8px;
  position: relative;
  z-index: 1;
}

@media (max-width: 768px) {
  .salary-detail-stats {
    grid-template-columns: 1fr;
  }
}

.salary-stat-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.salary-stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.salary-stat-icon.earn {
  background: #d1fae5;
  color: #059669;
}

.salary-stat-icon.date {
  background: #dbeafe;
  color: #2563eb;
}

.salary-stat-icon.group {
  background: #ede9fe;
  color: #7c3aed;
}

.salary-stat-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
  margin-bottom: 2px;
}

.salary-stat-value {
  display: block;
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

.salary-stat-value.muted {
  color: #94a3b8;
  font-weight: 500;
}

.salary-detail-tabs {
  display: flex;
  gap: 8px;
  padding: 20px 20px 0;
  border-bottom: 1px solid #f1f5f9;
}

.salary-detail-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 18px;
  border: none;
  background: none;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: color 0.2s, border-color 0.2s;
}

.salary-detail-tab:hover:not(:disabled) {
  color: #4f46e5;
}

.salary-detail-tab.active {
  color: #4f46e5;
  border-bottom-color: #4f46e5;
}

.salary-detail-tab.disabled,
.salary-detail-tab:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.salary-detail-body {
  padding: 20px;
}

.salary-cta-card {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid #e2e8f0;
  flex-wrap: wrap;
}

.salary-cta-card.warn {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-color: #fde68a;
}

.salary-cta-card.success {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-color: #a7f3d0;
}

.salary-cta-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.salary-cta-card.warn .salary-cta-icon {
  background: #fef3c7;
  color: #d97706;
}

.salary-cta-card.success .salary-cta-icon {
  background: #a7f3d0;
  color: #059669;
}

.salary-cta-card > div:nth-child(2) {
  flex: 1;
  min-width: 200px;
}

.salary-cta-card strong {
  display: block;
  font-size: 15px;
  color: #1e293b;
  margin-bottom: 4px;
}

.salary-cta-card p {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.salary-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  white-space: nowrap;
  transition: transform 0.15s, box-shadow 0.15s;
}

.salary-cta-btn.primary {
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

.salary-cta-btn.primary:hover:not(:disabled) {
  transform: translateY(-1px);
}

.salary-cta-btn.outline {
  background: #fff;
  border: 1px solid #a7f3d0;
  color: #059669;
}

.salary-cta-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.salary-tab-empty,
.salary-detail-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  color: #94a3b8;
}

.salary-detail-empty-icon,
.salary-tab-empty i {
  font-size: 48px;
  margin-bottom: 12px;
  opacity: 0.4;
}

.salary-detail-empty h4 {
  margin: 0 0 8px;
  color: #475569;
  font-size: 16px;
}

.salary-detail-empty p,
.salary-tab-empty p {
  margin: 0;
  font-size: 13px;
  max-width: 280px;
}

.hr-inline-spinner.dark {
  border-color: rgba(5, 150, 105, 0.25);
  border-top-color: #059669;
}
</style>
